import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, CheckCircle2, Loader2 } from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import WhatsAppFAB from "@/components/shared/WhatsAppFAB";

export default function CoordinationReceivedPage() {
  const { id } = useParams();
  const { t } = useI18n();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/bookings/${id}/public`)
      .then((r) => setBooking(unwrap(r)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="coordination-received-page">
      <SEO title="Request received — Ever Clean Home" description="We received your booking request and our coordination team is finding the right cleaner for your area." noindex />
      <Header />
      <WhatsAppFAB />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", duration: 0.7 }}
          className="w-24 h-24 rounded-full bg-[#0EA5A4]/10 grid place-items-center mx-auto" data-testid="coordination-icon">
          <Sparkles className="w-11 h-11 text-[#0EA5A4]" />
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="mt-6 text-center font-display text-4xl md:text-5xl font-extrabold tracking-tighter" data-testid="coordination-title">
          {t("coordination.title")}
        </motion.h1>

        {loading ? (
          <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <p className="mt-4 text-center text-[#4B5563] leading-relaxed">{t("coordination.body1")}</p>
            <p className="mt-3 text-center text-[#4B5563] leading-relaxed">{t("coordination.body2")}</p>

            <Card className="rounded-2xl border border-[#E5E7EB] mt-8 p-6 text-center" data-testid="coordination-card">
              <div className="text-xs uppercase tracking-wider font-bold text-[#4B5563]">{t("coordination.ref_label")}</div>
              <div className="mt-2 font-mono text-3xl font-bold text-[#0EA5A4] select-all" data-testid="coordination-booking-number">
                {booking?.booking_number || "—"}
              </div>
            </Card>

            <ul className="mt-6 space-y-2.5 text-sm max-w-md mx-auto">
              <li className="flex items-start gap-2.5"><CheckCircle2 className="w-5 h-5 text-[#0EA5A4] shrink-0 mt-0.5" /><span className="text-[#0C1A1A]">{t("coordination.bullet1")}</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 className="w-5 h-5 text-[#0EA5A4] shrink-0 mt-0.5" /><span className="text-[#0C1A1A]">{t("coordination.bullet2")} <b>{booking?.customer_email || ""}</b></span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 className="w-5 h-5 text-[#0EA5A4] shrink-0 mt-0.5" /><span className="text-[#0C1A1A]">{t("coordination.bullet3")}</span></li>
            </ul>
          </motion.div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/book">
            <Button className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="coordination-book-another">
              {t("coordination.book_another")} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <Link to="/">
            <Button variant="outline" className="rounded-xl" data-testid="coordination-home">{t("coordination.home")}</Button>
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
